const bySelector = (selector) => document.querySelector(selector);
const allBySelector = (selector) => [...document.querySelectorAll(selector)];
const activeCurrency = typeof Shopify !== "undefined" && Shopify.currency ? Shopify.currency.active : "GBP";
const moneyFormatter = new Intl.NumberFormat("en-GB", { style: "currency", currency: activeCurrency });

function setDrawer(selector, isOpen) {
  const drawer = bySelector(selector);
  if (!drawer) return;
  drawer.classList.toggle("is-open", isOpen);
  drawer.setAttribute("aria-hidden", String(!isOpen));
}

function formatMoney(cents) {
  return moneyFormatter.format((cents || 0) / 100);
}

const wishlistStorageKey = "velocity-wishlist";

function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (character) => {
    const entities = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;" };
    return entities[character];
  });
}

function getWishlist() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(wishlistStorageKey) || "[]");
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
}

function saveWishlist(items) {
  try {
    window.localStorage.setItem(wishlistStorageKey, JSON.stringify(items));
  } catch {
    // Storage can be unavailable in some private preview contexts.
  }
}

function syncWishlistButtons() {
  const savedIds = new Set(getWishlist().map((item) => String(item.id)));

  allBySelector("[data-wishlist-product]").forEach((button) => {
    const isSaved = savedIds.has(String(button.dataset.wishlistProductId));
    const title = button.dataset.wishlistTitle || "product";
    button.classList.toggle("is-active", isSaved);
    button.setAttribute("aria-pressed", String(isSaved));
    button.setAttribute("aria-label", `${isSaved ? "Remove" : "Add"} ${title} ${isSaved ? "from" : "to"} wishlist`);
  });
}

function renderWishlist() {
  const items = getWishlist();
  const count = bySelector(".wishlist-count");
  const container = bySelector(".wishlist-items");

  if (count) {
    count.textContent = items.length;
    count.hidden = items.length === 0;
  }

  if (!container) return;
  if (!items.length) {
    container.innerHTML = '<p class="empty-wishlist">Your wishlist is empty.</p>';
    return;
  }

  container.innerHTML = items
    .map(
      (item) => `
        <article class="wishlist-item">
          <a class="wishlist-item__link" href="${escapeHtml(item.url)}">
            ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">` : ""}
            <span>
              <strong>${escapeHtml(item.title)}</strong>
              <small>${escapeHtml(item.price)}</small>
            </span>
          </a>
          <button class="wishlist-item__remove" type="button" data-wishlist-remove="${escapeHtml(item.id)}" aria-label="Remove ${escapeHtml(item.title)} from wishlist">×</button>
        </article>
      `,
    )
    .join("");
}

function toggleWishlist(button) {
  const product = {
    id: button.dataset.wishlistProductId,
    handle: button.dataset.wishlistHandle,
    title: button.dataset.wishlistTitle,
    url: button.dataset.wishlistUrl,
    image: button.dataset.wishlistImage,
    price: button.dataset.wishlistPrice,
  };
  const items = getWishlist();
  const existingIndex = items.findIndex((item) => String(item.id) === String(product.id));

  if (existingIndex >= 0) {
    items.splice(existingIndex, 1);
  } else {
    items.push(product);
  }

  saveWishlist(items);
  renderWishlist();
  syncWishlistButtons();
}

function productVariantOptions(variant) {
  return variant.options || [variant.option1, variant.option2, variant.option3].filter((option) => option != null);
}

function readProductVariants(form) {
  const script = form.closest(".product-page__info")?.querySelector("[data-product-variants]");
  if (!script) return [];

  try {
    const variants = JSON.parse(script.textContent || "[]");
    return Array.isArray(variants) ? variants : [];
  } catch {
    return [];
  }
}

function getSelectedProductOptions(form) {
  return [...form.querySelectorAll("[data-product-option]")].map((group) => group.querySelector(".is-selected")?.dataset.optionValue || "");
}

function updateProductVariant(form, variants) {
  if (!variants.length) return;

  const selectedOptions = getSelectedProductOptions(form);
  const complete = selectedOptions.every(Boolean);
  const variant = complete ? variants.find((variant) => {
    const options = productVariantOptions(variant);
    return !selectedOptions.length || selectedOptions.every((value, index) => options[index] === value);
  }) : null;
  const hiddenVariant = form.querySelector("[data-variant-id]");
  const price = bySelector("[data-product-price]");
  const klarnaAmount = bySelector("[data-klarna-amount]");
  const clearpayAmount = bySelector("[data-clearpay-amount]");
  const submit = form.querySelector("[data-product-submit]");
  const paymentActions = form.querySelector(".product-payment-actions");
  const selectionMessage = form.querySelector("[data-product-selection-message]");

  if (hiddenVariant) {
    hiddenVariant.value = variant?.id || "";
    hiddenVariant.disabled = !variant?.available;
    hiddenVariant.dispatchEvent(new Event("change", { bubbles: true }));
  }
  if (variant && price) price.textContent = formatMoney(variant.price);
  if (variant && klarnaAmount) klarnaAmount.textContent = formatMoney(Math.round(Number(variant.price) / 3));
  if (variant && clearpayAmount) clearpayAmount.textContent = formatMoney(Math.round(Number(variant.price) / 4));
  if (paymentActions) paymentActions.hidden = !variant?.available;
  if (selectionMessage) selectionMessage.hidden = complete;
  if (submit) {
    submit.disabled = !variant?.available;
    submit.textContent = !complete ? form.dataset.chooseOptions : variant?.available ? "ADD TO CART" : "SOLD OUT";
  }

  form.querySelectorAll("[data-product-option]").forEach((group, optionIndex) => {
    const currentValue = selectedOptions[optionIndex];
    group.querySelectorAll("[data-option-value]").forEach((button) => {
      const value = button.dataset.optionValue;
      const available = variants.some((candidate) => {
        const options = productVariantOptions(candidate);
        return candidate.available && options[optionIndex] === value && selectedOptions.every((selected, index) => index === optionIndex || !selected || options[index] === selected);
      });
      const isSelected = value === currentValue;
      button.disabled = !available && !isSelected;
      button.classList.toggle("is-selected", isSelected);
      button.setAttribute("aria-pressed", String(isSelected));
    });
  });
}

function setupProductVariantForm(form) {
  const variants = readProductVariants(form);
  if (!variants.length) return;

  form.querySelectorAll("[data-product-option]").forEach((group) => {
    group.querySelectorAll("[data-option-value]").forEach((button) => {
      button.classList.remove("is-selected");
      button.setAttribute("aria-pressed", "false");
    });
  });

  form.addEventListener("click", (event) => {
    const optionButton = event.target.closest("[data-option-value]");
    if (!optionButton || optionButton.disabled) return;
    const group = optionButton.closest("[data-product-option]");
    group?.querySelectorAll("[data-option-value]").forEach((button) => {
      const isSelected = button === optionButton;
      button.classList.toggle("is-selected", isSelected);
      button.setAttribute("aria-pressed", String(isSelected));
    });
    updateProductVariant(form, variants);
  });

  updateProductVariant(form, variants);
}

function renderCart(cart) {
  const count = bySelector(".cart-count");
  const items = bySelector(".cart-items");
  const total = bySelector(".cart-total strong");

  if (count) count.textContent = cart.item_count;
  if (total) total.textContent = formatMoney(cart.total_price);

  if (!items) return;
  if (!cart.items.length) {
    items.innerHTML = '<p class="empty-cart">Your cart is empty.</p>';
    return;
  }

  items.innerHTML = cart.items
    .map(
      (item, index) => `
        <div class="cart-line" data-cart-line="${index + 1}">
          <div>
            <strong>${escapeHtml(item.product_title)}</strong>
            <small>${item.quantity} × ${formatMoney(item.final_price)}</small>
          </div>
          <div class="cart-line__purchase">
            <div class="quantity-control quantity-control--drawer" aria-label="Quantity for ${escapeHtml(item.product_title)}">
              <button type="button" data-cart-change data-cart-line="${index + 1}" data-cart-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity of ${escapeHtml(item.product_title)}">-</button>
              <span aria-live="polite">${item.quantity}</span>
              <button type="button" data-cart-change data-cart-line="${index + 1}" data-cart-quantity="${item.quantity + 1}" aria-label="Increase quantity of ${escapeHtml(item.product_title)}">+</button>
            </div>
            <strong>${formatMoney(item.final_line_price)}</strong>
          </div>
        </div>
      `,
    )
    .join("");
}

async function refreshCart() {
  const response = await fetch("/cart.js", { headers: { Accept: "application/json" } });
  if (!response.ok) return;
  renderCart(await response.json());
}

async function changeCartQuantity(line, quantity, button) {
  if (!line || Number.isNaN(quantity)) return;
  const originalDisabled = button?.disabled || false;
  if (button) button.disabled = true;

  try {
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ line, quantity: Math.max(0, quantity) }),
    });

    if (!response.ok) throw new Error("Unable to update quantity");
    await refreshCart();
  } finally {
    if (button) button.disabled = originalDisabled;
  }
}

async function addToCart(variantId, button) {
  if (!variantId) return;
  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = "Adding";

  try {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
    });

    if (!response.ok) throw new Error("Unable to add item");
    await refreshCart();
    setDrawer(".cart-drawer", true);
  } catch {
    button.textContent = "Try again";
    window.setTimeout(() => {
      button.textContent = originalText;
      button.disabled = false;
    }, 1500);
    return;
  }

  button.textContent = originalText;
  button.disabled = false;
}

async function renderSearch(query = "") {
  const results = bySelector(".search-results");
  if (!results) return;
  const normalized = query.trim();

  if (normalized.length < 2) {
    results.innerHTML = "";
    return;
  }

  const url = `/search/suggest.json?q=${encodeURIComponent(normalized)}&resources[type]=product&resources[limit]=6`;
  const response = await fetch(url, { headers: { Accept: "application/json" } });
  if (!response.ok) return;
  const data = await response.json();
  const products = data.resources?.results?.products || [];

  results.innerHTML = products
    .map(
      (product) => `
        <a class="search-result" href="${product.url}">
          <span>${product.title}</span>
          <strong>${product.price}</strong>
        </a>
      `,
    )
    .join("");
}

function boot() {
  bySelector(".menu-toggle")?.addEventListener("click", () => {
    bySelector(".mobile-menu")?.classList.add("is-open");
    bySelector(".mobile-menu")?.setAttribute("aria-hidden", "false");
    bySelector(".menu-toggle")?.setAttribute("aria-expanded", "true");
  });
  bySelector(".menu-close")?.addEventListener("click", () => {
    bySelector(".mobile-menu")?.classList.remove("is-open");
    bySelector(".mobile-menu")?.setAttribute("aria-hidden", "true");
    bySelector(".menu-toggle")?.setAttribute("aria-expanded", "false");
  });
  bySelector(".search-toggle")?.addEventListener("click", () => setDrawer(".search-drawer", true));
  bySelector(".search-close")?.addEventListener("click", () => setDrawer(".search-drawer", false));
  bySelector(".cart-toggle")?.addEventListener("click", () => setDrawer(".cart-drawer", true));
  bySelector(".cart-close")?.addEventListener("click", () => setDrawer(".cart-drawer", false));

  const wishlistToggle = bySelector(".wishlist-toggle");
  wishlistToggle?.addEventListener("click", () => {
    renderWishlist();
    setDrawer(".wishlist-drawer", true);
    wishlistToggle.setAttribute("aria-expanded", "true");
  });
  bySelector(".wishlist-close")?.addEventListener("click", () => {
    setDrawer(".wishlist-drawer", false);
    wishlistToggle?.setAttribute("aria-expanded", "false");
  });

  allBySelector(".drawer").forEach((drawer) => {
    drawer.addEventListener("click", (event) => {
      if (event.target === drawer) {
        setDrawer(`.${drawer.classList[1]}`, false);
        if (drawer.classList.contains("wishlist-drawer")) wishlistToggle?.setAttribute("aria-expanded", "false");
      }
    });
  });

  document.addEventListener("click", (event) => {
    const cartChangeButton = event.target.closest("[data-cart-change]");
    if (cartChangeButton) {
      changeCartQuantity(
        Number(cartChangeButton.dataset.cartLine),
        Number(cartChangeButton.dataset.cartQuantity),
        cartChangeButton,
      );
      return;
    }

    const cartPageQuantityButton = event.target.closest("[data-cart-page-quantity-change]");
    if (cartPageQuantityButton) {
      const control = cartPageQuantityButton.closest("[data-cart-page-quantity]");
      const input = control?.querySelector("input[type=number]");
      if (input) {
        const currentQuantity = Number(input.value) || 0;
        const direction = cartPageQuantityButton.dataset.cartPageQuantityChange === "increase" ? 1 : -1;
        input.value = Math.max(0, currentQuantity + direction);
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
      return;
    }

    const button = event.target.closest("[data-add-to-cart]");
    if (button) addToCart(button.dataset.addToCart, button);

    const wishlistButton = event.target.closest("[data-wishlist-product]");
    if (wishlistButton) toggleWishlist(wishlistButton);

    const removeButton = event.target.closest("[data-wishlist-remove]");
    if (removeButton) {
      const remaining = getWishlist().filter((item) => String(item.id) !== String(removeButton.dataset.wishlistRemove));
      saveWishlist(remaining);
      renderWishlist();
      syncWishlistButtons();
    }
  });

  allBySelector("[data-product-form]").forEach((form) => {
    setupProductVariantForm(form);
    form.addEventListener("submit", (event) => {
      if (!form.querySelector("[data-variant-id]")?.value || !getSelectedProductOptions(form).every(Boolean)) {
        event.preventDefault();
        form.querySelector("[data-product-option]:not(:has(.is-selected)) [data-option-value]:not(:disabled)")?.focus();
        return;
      }
      if (event.submitter?.closest(".shopify-payment-button") || document.activeElement?.closest(".shopify-payment-button")) return;
      event.preventDefault();
      const submit = form.querySelector("[data-product-submit]");
      if (!submit || submit.disabled) return;
      addToCart(form.querySelector("[data-variant-id]")?.value, submit);
    });
  });

  renderWishlist();
  syncWishlistButtons();

  bySelector("#SearchInput")?.addEventListener("input", (event) => renderSearch(event.target.value));

  const filterDrawer = bySelector("[data-filter-drawer]");
  const filterToggle = bySelector("[data-filter-toggle]");
  const filterCloseButtons = allBySelector("[data-filter-close]");

  const setFilterDrawer = (isOpen) => {
    if (!filterDrawer) return;
    filterDrawer.classList.toggle("is-open", isOpen);
    filterToggle?.setAttribute("aria-expanded", String(isOpen));
    document.body.classList.toggle("filters-open", isOpen);
  };

  filterToggle?.addEventListener("click", () => setFilterDrawer(true));
  filterCloseButtons.forEach((button) => button.addEventListener("click", () => setFilterDrawer(false)));

  const gridContainer = bySelector("[data-grid-container]");
  const gridViewButtons = allBySelector("[data-grid-view]");

  if (gridContainer && gridViewButtons.length) {
    const gridStorageKey = "velocity-collection-grid-view";
    const supportedViews = ["large", "standard", "compact"];
    const normaliseGridView = (view) => {
      if (view === "single") return "large";
      if (view === "double") return "standard";
      return supportedViews.includes(view) ? view : "standard";
    };

    const setGridView = (view, persist = true) => {
      const activeView = normaliseGridView(view);
      supportedViews.forEach((option) => {
        gridContainer.classList.toggle(`is-grid-${option}`, activeView === option);
      });
      gridViewButtons.forEach((button) => {
        button.setAttribute("aria-pressed", String(button.dataset.gridView === activeView));
      });

      if (persist) {
        try {
          window.localStorage.setItem(gridStorageKey, activeView);
        } catch {
          // Storage can be unavailable in some private preview contexts.
        }
      }
    };

    let savedGridView = "standard";
    try {
      savedGridView = window.localStorage.getItem(gridStorageKey) || savedGridView;
    } catch {
      // Use the default grid when storage is unavailable.
    }

    setGridView(savedGridView, false);
    gridViewButtons.forEach((button) => {
      button.addEventListener("click", () => setGridView(button.dataset.gridView));
    });
  }
}

boot();


document.addEventListener("click", (event) => {
  const trigger = event.target.closest("[data-payment-modal-open]");
  if (!trigger) return;
  const modal = document.getElementById(trigger.dataset.paymentModalOpen);
  if (!modal || modal.open) return;
  modal.showModal();
  document.documentElement.classList.add("nt-payment-modal-active");
});
document.querySelectorAll("[data-payment-info-modal]").forEach((modal) => {
  modal.querySelector("[data-payment-modal-close]").addEventListener("click", () => modal.close());
  modal.addEventListener("click", (event) => {
    if (event.target !== modal) return;
    const rect = modal.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) modal.close();
  });
  modal.addEventListener("close", () => document.documentElement.classList.remove("nt-payment-modal-active"));
});
